import { useMemo } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

import Styles from '../FormulaEditor.module.css';

import type { FormulaEditorObjectSource, ITokenFields } from './helpers';
import { getStringByTokens, STRIP_SHIELD, TokenNamesEnum } from './helpers';

export const useColoredString = ({
    field,
    isInvalidStatus,
    isTooltipHidden,
    isAvailableWithEmptyVariables,
    objectSource = {},
    maxIntegerPartLength,
    maxFractionalPartLength,
}: {
    field: ControllerRenderProps<FieldValues, any>;
    isInvalidStatus: boolean;
    isTooltipHidden?: boolean;
    isAvailableWithEmptyVariables?: boolean;
    objectSource: FormulaEditorObjectSource | undefined;
    maxIntegerPartLength?: number;
    maxFractionalPartLength?: number;
}) => {
    const fullStringValue = field.value ?? '';

    const tooltipIsActive = useMemo(() => isInvalidStatus && !isTooltipHidden, [isInvalidStatus, isTooltipHidden]);
    const validObjects = Object.keys(objectSource);

    const result = getStringByTokens({
        sourceString: fullStringValue,
        validObjectKeys: validObjects,
        maxIntegerPartLength,
        maxFractionalPartLength,
    });

    const isValidObjectNotExists = !validObjects.length;

    return useMemo(
        () =>
            isValidObjectNotExists && !isAvailableWithEmptyVariables
                ? fullStringValue
                : result?.components?.map((component) => {
                      if (component.hasError && tooltipIsActive) {
                          return renderInvalidToken({ token: component, isActive: tooltipIsActive });
                      }

                      if (
                          component.type === TokenNamesEnum.LEFT_BRACE ||
                          component.type === TokenNamesEnum.RIGHT_BRACE
                      ) {
                          return (
                              <span key={component.index} className={Styles.braceOp}>
                                  <span>{component.content}</span>
                              </span>
                          );
                      }

                      if (component.type === TokenNamesEnum.MATH) {
                          return (
                              <span key={component.index} className={Styles.mathOp}>
                                  <span>{component.content}</span>
                              </span>
                          );
                      }

                      if (component.type === TokenNamesEnum.VARIABLE) {
                          if (!isShielded(component.content)) {
                              return (
                                  <span key={component.index} className={Styles.variable}>
                                      {component.content}
                                  </span>
                              );
                          }

                          return (
                              <span key={component.index} className={Styles.variable}>
                                  <span className={Styles.variableHelpers}>[</span>
                                  <span>{component.content.replace(STRIP_SHIELD, '')}</span>
                                  <span className={Styles.variableHelpers}>]</span>
                              </span>
                          );
                      }

                      if (component.type === TokenNamesEnum.SPACE) {
                          if (component.hasError) {
                              return renderInvalidToken({
                                  token: component,
                                  isActive: tooltipIsActive,
                              });
                          }

                          return component.content;
                      }

                      return component.content;
                  }),
        [fullStringValue, isAvailableWithEmptyVariables, isValidObjectNotExists, result?.components, tooltipIsActive],
    );
};

const isShielded = (content: string) => content.startsWith('[') && content.endsWith(']');

const renderInvalidToken = ({ token, isActive }: { token: ITokenFields; isActive?: boolean }) => {
    const errorBackStyle = isActive ? Styles.errorClass : '';
    const errorTextStyle = isActive ? Styles.errorTextStyle : '';

    if (token.type === TokenNamesEnum.VARIABLE) {
        if ((token.headError || token.tailError) && isShielded(token.content)) {
            const headClass = token.headError ? `${Styles.variableHelpers} ${errorBackStyle}` : Styles.variableHelpers;
            const tailClass = token.tailError ? `${Styles.variableHelpers} ${errorBackStyle}` : Styles.variableHelpers;

            return (
                <span key={token.index} className={Styles.variable}>
                    <span className={headClass}>[</span>
                    <span>{token.content.replace(STRIP_SHIELD, '')}</span>
                    <span className={tailClass}>]</span>
                </span>
            );
        }

        return (
            <span key={token.index} className={`${errorBackStyle} ${errorTextStyle}`}>
                {token.content}
            </span>
        );
    }

    if (
        token.type === TokenNamesEnum.SPACE ||
        token.type === TokenNamesEnum.OTHER ||
        token.type === TokenNamesEnum.MATH ||
        token.type === TokenNamesEnum.DIGIT
    ) {
        return (
            <span key={token.index} className={`${errorBackStyle} ${errorTextStyle}`}>
                {token.content}
            </span>
        );
    }

    if (token.type === TokenNamesEnum.LEFT_BRACE || token.type === TokenNamesEnum.RIGHT_BRACE) {
        return (
            <span key={token.index} className={`${errorBackStyle} ${errorTextStyle}`}>
                {token.content}
            </span>
        );
    }

    return token.content;
};
